
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { DefaultFilters } from "@/data/filters/vaultAssets";
import { DefaultSorters } from "@/data/sorters/vaultAssets";
import { ConfirmModalModes } from "@/data/enums";

// Types
import {
  IVaultProvider,
  IVaultProviderMethods
} from "@/models/providers/vault";
import { IVaultAsset } from "@/models/vaultAssets";
import { ITableConfig, IFilter, ISorter } from "@/models/tables";
import { IClient } from "@/models/clients";
import { IAccount } from "@/models/accounts";
import { IContractProduct } from "@/models/contracts";
import { ILead } from "@/models/leads";

@Component
export default class VaultProvider extends Mixins(FilteringSortingTableStore) {
  // Props

  @Prop({ default: null })
  public clientId!: IClient["id"];

  @Prop({ default: null })
  public accountId!: IAccount["id"];

  @Prop({ default: null })
  public leadId!: ILead["id"];

  @Prop({ default: null })
  public contractProductId!: IContractProduct["id"];

  @Prop({
    default: `VP` /** [V]ault [P]rovider */
  })
  public declare id: string;

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => DefaultFilters() })
  public availableFilters!: IFilter[];

  @Prop({ default: () => DefaultSorters() })
  public availableSorters!: ISorter[];

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  @Prop({ default: 12 })
  public declare limit: number;

  // Data

  public dataModule = this.isClientVault
    ? DataModules.CLIENTS_VAULT_ASSETS
    : DataModules.LEADS_VAULT_ASSETS;

  public defaults: ITableConfig = {
    page: 1,
    limit: this.limit,
    sort: {},
    filters: []
  };
  public filters = this.availableFilters;
  public sorters = this.availableSorters;
  public isReloading = false;

  // Life-cycle Methods

  public created() {
    if (!this.contentIsAvailable) return;

    this.dataPayloadSettings = { id: this.ownerId };
    this.getData();

    if (_.has(this.manualFilters, "filter[encrypted]")) {
      this.$bus.$on(
        this.manualFilters["filter[encrypted]"]
          ? "vaultAssetsRefreshSecrets"
          : "vaultAssetsRefreshNotes",
        () => this.refreshData()
      );
    }
  }

  public beforeDestroy() {
    if (_.has(this.manualFilters, "filter[encrypted]")) {
      this.$bus.$off(
        this.manualFilters["filter[encrypted]"]
          ? "vaultAssetsRefreshSecrets"
          : "vaultAssetsRefreshNotes"
      );
    }
  }

  // Methods

  public async reloadData() {
    this.isReloading = true;
    await this.refreshData();
  }

  public refreshData() {
    return this.getData({ ignoreStored: true });
  }

  // Overrides mixin method
  public getStoreParams(payload: any): Promise<any> {
    // Here we delete order and let BE handle pinned ordering
    delete payload?.params?.order;
    return this.$_.merge(payload, {
      params: {
        with: [
          "contract_product",
          "contract_product.product.image",
          "contract_product.product.brand.currency"
        ].join(),
        ...this.contractProductFilter,
        ...this.manualFilters
      }
    });
  }

  public createVaultAsset(data) {
    if (!this.canCreate) return;
    return this.$store.dispatch(`data/${this.dataModule}/create`, {
      id: this.ownerId,
      data
    });
  }

  public removeVaultAsset(assetId: IVaultAsset["id"]) {
    if (!this.canDelete) return;

    return this.$store.dispatch(`data/${this.dataModule}/remove`, {
      id: this.ownerId,
      assetId
    });
  }

  public async updateVaultAsset(assetId: IVaultAsset["id"], data: object) {
    if (!this.canUpdate) return;

    try {
      await this.$store.dispatch(`data/${this.dataModule}/update`, {
        id: this.ownerId,
        assetId,
        data
      });

      await this.refreshData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public openUpdateAssetModal({
    asset,
    isConvertingToSecret = false,
    events = {
      success: this.refreshData
    }
  }: {
    asset: IVaultAsset;
    isConvertingToSecret?: boolean;
    events?: object;
  }) {
    if (!(_.isNull(asset) ? this.canCreate : this.canUpdate)) return;

    this.$store.dispatch(
      `data/${DataModules.CLIENTS_VAULT_ASSETS}/openManageModal`,
      {
        props: {
          asset,
          clientId: this.clientId,
          accountId: this.accountId,
          leadId: this.leadId,
          isConvertingToSecret
        },
        events
      }
    );
  }

  public deleteNote(assetId: IVaultAsset["id"]) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<boolean>(async resolve => {
      if (!this.canDelete) return resolve(false);
      const confirm = await this.$store.dispatch("ui/open/confirmModal", {
        config: {
          props: {
            autoClose: false,
            confirmButtonText: this.$t("_action.delete"),
            message: this.$t("_sentence.vault.confirm_delete_note"),
            mode: ConfirmModalModes.BUTTON,
            title: this.$t("_action.delete_note")
          },
          events: {
            close: () => resolve(false),
            confirmed: async () => {
              try {
                await this.removeVaultAsset(assetId);
                this.refreshData();
                this.$store.dispatch("toast/show", {
                  message: this.$t("_sentence.vault.note_deleted")
                });
                resolve(true);
              } catch (error) {
                resolve(false);
                this.$store.dispatch("api/handleError", error);
              } finally {
                confirm.close();
              }
            }
          }
        }
      });
    });
  }

  public deleteSecret(assetId: IVaultAsset["id"]) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<boolean>(async resolve => {
      if (!this.canDelete) return resolve(false);
      const confirm = await this.$store.dispatch("ui/open/confirmModal", {
        config: {
          props: {
            autoClose: false,
            confirmButtonText: this.$t("_action.delete"),
            message: this.$t("_sentence.vault.confirm_delete_secret"),
            mode: ConfirmModalModes.BUTTON,
            title: this.$t("_action.delete_secret")
          },
          events: {
            close: () => resolve(false),
            confirmed: async () => {
              try {
                await this.removeVaultAsset(assetId);
                this.refreshData();
                this.$store.dispatch("toast/show", {
                  message: this.$t("_sentence.vault.secret_deleted")
                });
                resolve(true);
              } catch (error) {
                resolve(false);
                this.$store.dispatch("api/handleError", error);
              } finally {
                confirm.close();
              }
            }
          }
        }
      });
    });
  }

  public decryptVaultAsset(assetId: IVaultAsset["id"]) {
    if (!this.canDecrypt) return;
    return this.$store.dispatch(`data/${this.dataModule}/decrypt`, {
      id: this.ownerId,
      assetId
    });
  }

  // Computed

  public get contractProductFilter() {
    return _.isNull(this.contractProductId)
      ? {}
      : { "filter[contract_product_id]": this.contractProductId };
  }

  public get isClientVault() {
    return _.isNull(this.leadId);
  }

  public get ownerId() {
    return this.isClientVault ? this.clientId : this.leadId;
  }

  // Guarding

  public get canCreate() {
    return this.isClientVault
      ? this.$userCan("create_client_vault_asset")
      : this.$userCan("create_lead_vault_asset");
  }

  public get canUpdate() {
    return this.isClientVault
      ? this.$userCan("update_client_vault_asset")
      : this.$userCan("update_lead_vault_asset");
  }

  public get canDelete() {
    return this.isClientVault
      ? this.$userCan("delete_client_vault_asset")
      : this.$userCan("delete_lead_vault_asset");
  }

  public get canDecrypt() {
    return this.isClientVault
      ? this.$userCan("decrypt_client_vault_asset")
      : this.$userCan("decrypt_lead_vault_asset");
  }

  public guardFunctionalities = this.isClientVault
    ? ["list_client_vault_assets"]
    : ["list_lead_vault_assets"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("vaultProvider")
  public providerData(): IVaultProvider {
    return {
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      assets: this.table.data as IVaultAsset[],
      availableFilters: this.filters,
      availableSorters: this.sorters,
      canCreate: this.canCreate,
      canDecrypt: this.canDecrypt,
      canDelete: this.canDelete,
      canUpdate: this.canUpdate,
      contentIsAvailable: this.contentIsAvailable,
      contractProductId: this.contractProductId,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      hasRequestError: this.requestError,
      isClientVault: this.isClientVault,
      isLoading: this.loading || this.table.loading,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      table: this.table,
      total: this.table.total
    };
  }

  @Provide("vaultProviderMethods")
  public get providerMethods(): IVaultProviderMethods {
    return {
      createVaultAsset: this.createVaultAsset,
      decryptVaultAsset: this.decryptVaultAsset,
      deleteNote: this.deleteNote,
      deleteSecret: this.deleteSecret,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      openUpdateAssetModal: this.openUpdateAssetModal,
      refreshData: this.refreshData,
      reloadData: this.reloadData,
      updateVaultAsset: this.updateVaultAsset
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: this.reloadData }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $vaultData: this.providerData(),
        $vaultMethods: this.providerMethods
      })
    );
  }
}
